import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { ReactComponent as RefreshIcon } from '../../../../assets/icons/dashboard/rotate-left.svg';
import { ReactComponent as LoaderIcon } from '../../../../assets/icons/loader.svg';
import { brandText } from '../../../../config/branding';
import currentTimeZone from '../../../../config/timeZone';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import { TooltipIcon } from '../../../../shared-components/tooltip/TooltipIcon';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  getBucketFromRange,
} from '../../../../shared-functions/ChartFunctions';
import { getRelativeTime } from '../../../../shared-functions/DateTime';
import dashboard from '../../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

interface RevenuePowerConsumedChartProperties {
  startDate: string;
  endDate: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * Component to display revenue vs power consumed chart
 * @param {any} param0 - The param0
 * @param {any} param0.startDate - The startDate prop
 * @param {any} param0.endDate - The endDate prop
 * @param {any} param0.filters - The filters prop
 * @returns {any} The result
 */
function RevenuePowerConsumedChart({
  startDate,
  endDate,
  filters,
}: RevenuePowerConsumedChartProperties) {
  const { t } = useTranslation('dashboard');
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const activeRangeReference = React.useRef<string>('');

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: `${t('revenue')}`,
        data: powerConsumedData.map((d) => Number(d.totalDebitAmount || 0)),
        borderColor: '#16A34A',
        /**
         * Create a linear gradient for the background
         * @param {any} context - The chart context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(22, 163, 74, 0.4)');
          gradient.addColorStop(1, 'rgba(22, 163, 74, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: `${t('electricity')}`,
        data: powerConsumedData.map((d) => Number(d.totalPowerConsumed || 0)),
        borderColor: '#F59E0B',
        /**
         * Create a linear gradient for the background
         * @param {any} context - The chart context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const chartContext = chart.ctx;
          const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
          gradient.addColorStop(0, 'rgba(245, 158, 11, 1)');
          gradient.addColorStop(1, 'rgba(245, 158, 11, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @param {any} context - The context
         * @returns {string} The result
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === 'Revenue') {
              return '#16A34A'; // Orange for Revenue
            }
            if (context.tooltipItems[0].dataset.label === 'Electricity') {
              return '#F59E0B'; // Green for Electricity
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param {any} context - The context
           * @returns {string} The result
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === 'Revenue' ? t('revenue') : t('electricity');
          },
          /**
           * Function to show tooltip data
           * @param {any} context - The context
           * @returns {string} The result
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === 'Revenue' ? brandText.currency : 'kW'}`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: true, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: true,
        },
      },
    },
  };

  /**
   * Skeleton chart for empty state
   * @returns {any} The result
   */
  function SkeletonLineChart() {
    const skeletonData = {
      labels: Array.from({ length: 12 }, (_, index) => index.toString()),
      datasets: [
        {
          label: '',
          data: Array.from({ length: 12 }, () => 0),
          borderColor: '#F1F5F9',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
        },
      ],
    };

    return (
      <div className="chart-wrapper skeleton-chart">
        <Line
          data={skeletonData}
          options={{
            ...options,
            plugins: { ...options.plugins, tooltip: { enabled: false } },
            scales: { ...options.scales, y: { ...options.scales.y, ticks: { display: false } } },
          }}
        />
      </div>
    );
  }

  /**
   * Fetch power consumed data based on the selected date range
   * @param {any} rangeStartDate - The range start date
   * @param {any} rangeEndDate - The range end date
   * @param {any} hardRefresh - The hardRefresh
   */
  const fetchPowerConsumedReport = async (
    rangeStartDate: string,
    rangeEndDate: string,
    hardRefresh = false,
  ) => {
    const rangeKey = `${rangeStartDate}_${rangeEndDate}`;
    activeRangeReference.current = rangeKey;
    try {
      setIsLoading(true);
      const { facilityIds, stationIds, deviceIds } = filters || {};
      const query = {
        startDate: rangeStartDate,
        endDate: rangeEndDate,
        timeZone: currentTimeZone,
        hardRefresh,
        accountCustomId: facilityIds,
        stationIds,
        deviceIds,
      };
      if (facilityIds && facilityIds?.length > 0) {
        query.accountCustomId = facilityIds;
      }
      if (stationIds && stationIds?.length > 0) {
        query.stationIds = stationIds;
      }
      if (deviceIds && deviceIds?.length > 0) {
        query.deviceIds = deviceIds;
      }
      const response = await dashboard.getPowerConsumedReport(query);

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== rangeKey) return;

      // ✅ Otherwise, update the chart with the right data
      const updatedTime = response.data?.last_updated_time;
      const result = response.data?.result;
      const { bucket, days } = getBucketFromRange(rangeStartDate, rangeEndDate);
      if (response.status === 200) {
        if (bucket === 'hour') {
          setPowerConsumedData(fillMissingLast24HrsGeneric(result));
        } else {
          setPowerConsumedData(
            fillMissingLastNDaysGeneric(result, days, 'en-US', rangeStartDate, rangeEndDate),
          );
        }
      }

      if (updatedTime) {
        setLastUpdatedTime(updatedTime);
      }
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === rangeKey) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport(startDate, endDate, true);
  }, [startDate, endDate, filters]);

  return (
    // ... (trimmed)
    <div className="revenue-power-consumer-chart-wrap">
      <div className="revenue-power-consumer-chart-header">
        <div className="header-title">
          <div className="title-wrapper">
            <h6>
              <span className="power-consumer">{t('powerConsumed')}</span>
              <span>{t('vs')}</span>
              <span className="revenue-growth">{t('revenueGrowth')}</span>
            </h6>
            <TooltipIcon text={t('powerConsumedAndRevenueGrowthChart')} infoColor="info" />
          </div>
          {/* <span className="header-arrow">{'->'}</span> */}
        </div>
        {/* <div className="label">
          <p>{t('commercialOnly')}</p>
        </div> */}
      </div>
      {isLoading ? (
        <center className="revenue-consumer-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="revenue-power-consumer-chart-container">
          {powerConsumedData.length === 0 ? (
            <SkeletonLineChart />
          ) : (
            <div className="chart-wrapper">
              <Line data={chartData} options={options} />
            </div>
          )}
        </div>
      )}
      <div className="refresh">
        {isRefreshing ? (
          <div className="loading">
            <LoaderIcon style={{ width: '20px', height: '20px' }} />
          </div>
        ) : (
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={() => {
              setIsRefreshing(true);
              fetchPowerConsumedReport(
                startDate,
                endDate,
                true, // hard refresh on button click
              );
            }}
            svgBackIcon={<RefreshIcon style={{ width: '20px', height: '20px' }} />}
          />
        )}
        <p className="updated-time">
          {t('lastUpdated')} : {lastUpdatedTime ? getRelativeTime(lastUpdatedTime, t) : ''}
        </p>
      </div>
    </div>
  );
}
RevenuePowerConsumedChart.defaultProps = {
  /**
   * Default props for RevenuePowerConsumedChart component
   */
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default RevenuePowerConsumedChart;
